import jsPDF from 'jspdf';
import { GenerateVendorInvoicePDFParams } from './types';
import { calculateInvoiceTotals, generateInvoiceMetadata, generateInvoiceFilename } from './invoiceCalculations';
import {
  addLogoToPDF,
  renderVendorInfo,
  renderInvoiceDetailsBox,
  renderMeterTable,
  renderSummaryTable,
  renderDetailedBreakdownTable,
  renderAdjustmentComments,
  renderMeterReadsPage,
} from './invoiceComponents';

/**
 * Build a basic vendor invoice document (without reviewed/NSP boxes)
 * This is the base invoice that can be used by vendors
 *
 * @param doc - Optional existing jsPDF document to add content to
 * @param startY - Optional starting Y position (defaults to 15)
 * @param dueDateOffsetDays - Days to subtract from the displayed due date (used by the NSP version)
 */
export const buildVendorInvoiceDocument = async (
  {
    vendor,
    primaryIssuer,
    monthYear,
    hstRate,
    applyHst = true,
  }: GenerateVendorInvoicePDFParams,
  doc?: jsPDF,
  startY: number = 15,
  dueDateOffsetDays: number = 0
): Promise<jsPDF> => {
  const document = doc || new jsPDF();
  const pageWidth = document.internal.pageSize.getWidth();
  const leftMargin = 14;
  let yPosition = startY;

  // Generate invoice metadata
  const metadata = generateInvoiceMetadata(vendor, monthYear, dueDateOffsetDays);

  // Calculate invoice totals
  const { meterSubtotal, subtotal, hstValue, total, totalNetGen, miscAmount } = calculateInvoiceTotals(vendor, hstRate);
  const miscHstApplicable = vendor.misc_charge?.hst_applicable ?? false;
  const miscReason = vendor.misc_charge?.reason ?? '';

  // LOGO AND INVOICE DETAILS (side by side)
  const logoHeight = await addLogoToPDF(document, primaryIssuer?.logoUrl, leftMargin, yPosition);
  const invoiceDetailsHeight = renderInvoiceDetailsBox(document, vendor, pageWidth - 81 - leftMargin, yPosition, metadata, monthYear);
  yPosition += Math.max(logoHeight, invoiceDetailsHeight) + 5;

  // VENDOR INFORMATION (REMIT TO)
  const vendorInfoHeight = renderVendorInfo(document, vendor, leftMargin, yPosition);
  yPosition += vendorInfoHeight;

  // MAIN METER TABLE
  yPosition = renderMeterTable(document, vendor, yPosition);

  // SUMMARY TABLE (SUBTOTAL, HST, TOTAL)
  yPosition = renderSummaryTable(document, yPosition, meterSubtotal, subtotal, hstValue, total, hstRate, applyHst, miscAmount, miscHstApplicable, miscReason);

  // DETAILED BREAKDOWN TABLE - Only render if needdetails_withinvoice is 'YES'
  if (vendor.needdetails_withinvoice?.toUpperCase() === 'YES') {
    yPosition = renderDetailedBreakdownTable(document, vendor, yPosition, subtotal, totalNetGen) + yPosition;
  }

  // ADJUSTMENT COMMENTS - Shown whenever any meter has adjustment comments
  renderAdjustmentComments(document, vendor, yPosition);

  // PAGE 2 - Meter read information
  renderMeterReadsPage(document, vendor, monthYear);

  return document;
};

/**
 * Build a vendor invoice document and return it as a Blob (for bundling into a ZIP)
 */
export const buildVendorInvoiceBlob = async (params: GenerateVendorInvoicePDFParams): Promise<{ blob: Blob; filename: string }> => {
  const doc = await buildVendorInvoiceDocument(params);
  const filename = generateInvoiceFilename(params.vendor.vendor_name, params.monthYear, 'Vendor - ');
  const blob = doc.output('blob');
  return { blob, filename };
};

/**
 * Build a vendor invoice document and return it as base64 (for emailing as an attachment)
 */
export const buildVendorInvoiceBase64 = async (params: GenerateVendorInvoicePDFParams): Promise<{ base64: string; filename: string }> => {
  const doc = await buildVendorInvoiceDocument(params);
  const filename = generateInvoiceFilename(params.vendor.vendor_name, params.monthYear, 'Vendor - ');
  const dataUri = doc.output('datauristring');
  const base64 = dataUri.substring(dataUri.indexOf('base64,') + 'base64,'.length);
  return { base64, filename };
};

/**
 * Generate a vendor invoice PDF and download it
 * Uses NSP invoice document (with boxes) by default for backward compatibility
 */
export const generateVendorInvoicePDF = async (params: GenerateVendorInvoicePDFParams): Promise<void> => {
  const doc = await buildVendorInvoiceDocument(params);

  // Generate filename and download
  const filename = generateInvoiceFilename(params.vendor.vendor_name, params.monthYear, 'Vendor - ');
  doc.save(filename);
};
